// Hosted Staff Web Link — single-page tool (§6.1).
//
// A logged-in (validatetokenrestaurantuser) tool with NO install — just a URL.
// Three sections: Show QR (rotating venue token), Cash stamp (STAFF_GRANT) and
// Redeem voucher (staff burn). Self-contained like every other page in this app:
// own axios calls, own localStorage key (`hw_staff_profile`), own dark/mobile-first
// styling. Fail-safe throughout — every action surfaces a clear inline error and
// an expired session bounces back to the login screen.
//
// Invariants honoured by the sections:
//   A4 — staff identity is the signed Bearer token (server reads req.decoded);
//        the client never sends a userid/restaurant as the identity of record.
//   A5 — the QR only LINKS a wallet; minting is STAFF_GRANT (Cash stamp) or
//        order-completion. The QR scan never bare-mints.
//   A6 — redemption is a staff-gated burn.
import React, { useCallback, useEffect, useState } from 'react';
import './styles.css';
import { getStaffSession, clearStaffSession, fetchVenueCampaigns, describeError } from './api';
import StaffLogin from './StaffLogin';
import ShowQr from './ShowQr';
import CashStamp from './CashStamp';
import RedeemVoucher from './RedeemVoucher';
import ScanTicket from './ScanTicket';

const TABS = [
  { key: 'qr', label: 'Show QR' },
  { key: 'cash', label: 'Cash stamp' },
  { key: 'redeem', label: 'Redeem' },
  // Ticket door scanning — TICKETING-SPEC.md §5.4. It rides on this tool rather
  // than growing a page of its own because the session, the scanner and the
  // venue scoping already exist here, and door staff are already signed in.
  { key: 'ticket', label: 'Scan ticket' },
];

export default function Staff() {
  const [session, setSession] = useState(() => getStaffSession());
  const [tab, setTab] = useState('qr');
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsError, setCampaignsError] = useState('');
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  const profile = (session && session.profile) || {};
  const restaurantId = profile.Restaurant || null;
  const otherRestaurantIds = Array.isArray(profile.OtherRestaurant)
    ? profile.OtherRestaurant.map((o) => o && o.Restaurant).filter(Boolean)
    : [];

  const handleAuthExpired = useCallback(() => {
    clearStaffSession();
    setSession(null);
  }, []);

  const logout = () => {
    clearStaffSession();
    setSession(null);
    setCampaigns([]);
    setTab('qr');
  };

  // Load the venue's active campaigns once we have a session (populates the
  // Show-QR + Cash-stamp pickers). Fail-safe: an error here doesn't block the
  // tool; Redeem voucher works without campaigns.
  useEffect(() => {
    if (!session) return;
    let alive = true;
    setLoadingCampaigns(true);
    setCampaignsError('');
    fetchVenueCampaigns(restaurantId, otherRestaurantIds)
      .then((list) => {
        if (alive) setCampaigns(list);
      })
      .catch((err) => {
        if (alive) setCampaignsError(describeError(err));
      })
      .finally(() => {
        if (alive) setLoadingCampaigns(false);
      });
    return () => {
      alive = false;
    };
    // restaurantId is stable for a session; re-run only on login/logout
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, restaurantId]);

  if (!session) {
    return <StaffLogin onLoggedIn={setSession} />;
  }

  return (
    <div className="staff-root">
      <div className="staff-shell">
        <div className="staff-topbar">
          <div className="staff-venue">
            <span className="staff-venue-name">{profile.RestaurantName || 'Your venue'}</span>
            <span className="staff-venue-sub">
              {profile.Name ? `${profile.Name} · ` : ''}Staff tool
            </span>
          </div>
          <button
            className="staff-btn staff-btn-danger"
            type="button"
            onClick={logout}
            style={{ width: 'auto', padding: '8px 14px' }}
          >
            Sign out
          </button>
        </div>

        <div className="staff-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`staff-tab${tab === t.key ? ' is-active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {campaignsError && (tab === 'qr' || tab === 'cash') ? (
          <div className="staff-alert is-warn">Couldn’t load campaigns: {campaignsError}</div>
        ) : null}

        {loadingCampaigns && (tab === 'qr' || tab === 'cash') ? (
          <div className="staff-alert is-warn">
            <span
              className="staff-spinner"
              style={{ borderColor: 'rgba(194, 65, 12, 0.25)', borderTopColor: '#c2410c' }}
            />
            Loading campaigns…
          </div>
        ) : null}

        {tab === 'qr' ? (
          <ShowQr
            restaurantId={restaurantId}
            campaigns={campaigns}
            onAuthExpired={handleAuthExpired}
          />
        ) : null}

        {tab === 'cash' ? (
          <CashStamp campaigns={campaigns} onAuthExpired={handleAuthExpired} />
        ) : null}

        {tab === 'redeem' ? <RedeemVoucher onAuthExpired={handleAuthExpired} /> : null}

        {tab === 'ticket' ? <ScanTicket onAuthExpired={handleAuthExpired} /> : null}

        <div className="staff-footnote">
          heywaitr · stamps earn on order completion or a staff grant — a scan never mints on its
          own. Ticket scans admit once and cannot be undone.
        </div>
      </div>
    </div>
  );
}
